import { AxiosResponse } from "axios";
import { ButtonText } from "components/Atoms/Button";
import { SearchInput } from "components/Atoms/SearchInput";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { LinkedArtistPanelInstructionBase } from "components/Picker/ArtistPicker/model";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import Table from "components/Tables/Table";
import React, { useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";

interface Props<T extends LinkedArtistPanelInstructionBase> {
  queryKey: string;
  perPage: number;
  title: string;
  renderLinkedArtistPanelMessage: (selected: number, total: number, onAddAll: () => Promise<void>) => React.ReactNode;
  listFunc: (instruction: T) => Promise<AxiosResponse<server.dto.ApiResponseWrapperWithRequest<server.dto.ArtistSmall[], server.dtofilters.ListLinkedArtistFilters>, any>>;
  initialInstruction: T;
  renderLastColumn?: (artist: server.dto.ArtistSmall) => React.ReactNode;
  onDelete: (artist: server.dto.ArtistSmall) => void;
  onRemoveAll?: () => Promise<AxiosResponse>;
  onAddAll?: () => Promise<AxiosResponse>;
  onAdd: () => void;
}

export const LinkedArtistsPanel = <T extends LinkedArtistPanelInstructionBase>(props: Props<T>) => {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [total, setTotal] = React.useState<number>(0);
  const [selected, setSelected] = React.useState<number>(0);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isRemovingAll, setIsRemovingAll] = useState<boolean>(false);
  const debouncedSearchQuery = useDebounce(searchQuery);

  const [instruction, setInstruction] = useState<T>({
    page: 1,
    perPage: props.perPage,
    ...props.initialInstruction,
  });

  React.useEffect(() => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: 1,
    });
  }, [debouncedSearchQuery]);

  const api = useCustomQuery({
    queryFn: () => props.listFunc(instruction),
    queryKey: ["artists", instruction, props.queryKey],
    onSuccess: (response) => {
      setTotal(response.data.absoluteCount);
      setSelected(response.data.selectedCount);
      setIsInitialLoading(false);
      pagination.setPagination(response.data.pagination);
    }
  });

  const onRemoveAll = async () => {
    setIsRemovingAll(true);
    await props.onRemoveAll?.();
    setIsRemovingAll(false);
    api.refetch();
  }

  const onAddAll = async () => {
    await props.onAddAll?.();
    api.refetch();
  }

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, props.perPage, paginate);
  const items = api.data?.data?.data || [];
  const displaySearchBar = selected > props.perPage;
  const displayRemoveAll = selected > 4 && props.onRemoveAll;
  const isLoading = api.isLoading;

  const onChange = () => {
    api.refetch();
  }

  return <SectionPanel.Panel
    header={<>
      <PanelSegment.Title title={props.title} />
      {selected < total && <PanelSegment.Button
        label={RESX.Buttons.Add}
        type="secondary"
        onClick={props.onAdd}
      />}
    </>}
  >
    <div className="flex-column gap">
      {!isInitialLoading && props.renderLinkedArtistPanelMessage(selected, total, onAddAll)}
      {displaySearchBar && <div>
        <SearchInput
          onChange={(_, { value }) => { setSearchQuery(value); }}
          value={searchQuery}
          placeholder={RESX.Company.SearchByName}
        />
      </div>}
      {pagination && <div className="flex justify-end">
        <PanelSegment.Pagination>{pagination.Component}</PanelSegment.Pagination>
      </div>}
      <Table
        variant="cards"
        hideNoDataAvailableDiv
        canDelete={() => true}
        onDelete={props.onDelete}
        items={items}
        isLoading={isLoading}
        getKey={artist => artist.artistId.toString()}
        hideHeader
        columns={[{
          key: "name",
          header: "",
          render: (artist) => <CompanySidePanel type="artist"
            companyId={artist.artistId}
            onOpen={onChange}
            trigger={<a>{artist.name}</a>}
          />,
          size: "1fr"
        }, ...props.renderLastColumn ? [{
          key: "preview",
          header: "",
          render: props.renderLastColumn,
        }] : []]}
      />
    </div>
    {displayRemoveAll && <div className="flex justify-end margin-top">
      <ButtonText
        className="no-padding"
        label={RESX.Buttons.RemoveAll}
        onClick={onRemoveAll}
        loading={isRemovingAll}
      />
    </div>}
  </SectionPanel.Panel>
}
